import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, parseApiError } from '../lib/api';
import { Card } from '../components/Card';
import { ClanBrandingHero } from '../components/ClanBrandingHero';
import { StatusBadge } from '../components/StatusBadge';
import { PageSpinner } from '../components/Spinner';
import { DisciplineFlagPanel } from '../components/DisciplineFlagPanel';
import { isAdminRole } from '../lib/roles';
import { ArrowLeft, Users, Crown, Gamepad2, Send, Loader2, CheckCircle2, DoorOpen } from 'lucide-react';
import type { Me } from '../lib/api';

export type ClanApplyState = 'member' | 'pending' | 'accepted' | 'declined' | 'login' | 'closed' | 'apply';

// ─────────────────────────────────────────────────────────────────────────────
// Apply-affordance gate for the clan-detail banner (JJN test, 2026-07-26: the
// QATest dev account — role:'public', zero CLAN# rows, already an ORG_MEMBER
// AND holding an existing pending CLAN application — fully authenticated,
// simply not yet IN a clan). The old `canApply` required `role !== 'public'`,
// which the backend's POST /clans/:name/applications never checks
// (portalApi/index.ts ~14453, "Any authenticated user can apply." — only
// clan-exists + publicApplicationsOpen + not-already-member + not-banned).
// This mirrors the backend exactly: authenticate plus the same real business
// conditions, nothing more. Precedence matters — closed beats everything
// (nothing to report on if applications aren't open at all), then member,
// then an existing application, then login, and only then apply.
// ─────────────────────────────────────────────────────────────────────────────
export function clanApplyState(args: {
  me: Pick<Me, 'role'> | null | undefined;
  publicApplicationsOpen: boolean | undefined;
  alreadyMember: boolean;
  myApp: { status: 'PENDING' | 'ACCEPTED' | 'REJECTED' } | null | undefined;
}): ClanApplyState {
  if (!args.publicApplicationsOpen) return 'closed';
  if (args.alreadyMember) return 'member';
  if (args.myApp) {
    if (args.myApp.status === 'PENDING') return 'pending';
    if (args.myApp.status === 'ACCEPTED') return 'accepted';
    return 'declined';
  }
  if (!args.me) return 'login';
  return 'apply';
}

// The actual reason, spelled out — a blocked-but-authenticated caller must
// never be told to log in again (that was the JJN/QATest bug's copy half:
// the frontend was mislabelling its own refusal as a login problem).
export function clanApplyCopy(state: ClanApplyState): string {
  switch (state) {
    case 'member': return "You're already a member of this clan.";
    case 'pending': return 'Your application is pending.';
    case 'accepted': return 'You were accepted!';
    case 'declined': return 'Your application was declined.';
    case 'closed': return 'Applications are closed.';
    case 'login': return 'Login to apply.';
    case 'apply': return 'This clan is accepting new members.';
  }
}

export function ClanDetail() {
  const { name } = useParams<{ name: string }>();
  const clanName = decodeURIComponent(name || '');
  const qc = useQueryClient();
  const [applyOpen, setApplyOpen] = useState(false);
  const [gamertag, setGamertag] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });
  const { data: clan, isLoading, error: clanError } = useQuery({
    queryKey: ['clan', clanName],
    queryFn: () => api.clan(clanName),
    enabled: !!clanName,
  });
  const { data: myApps } = useQuery({
    queryKey: ['myApplications'],
    queryFn: api.myApplications,
    // JJN test, 2026-07-26: was gated on role !== 'public' too — a
    // role:'public' caller (not yet in any clan) can still have applications
    // on file (the QATest account had a live pending CLAN application), so
    // gating this fetch on role hid their own application from them.
    enabled: !!me,
  });

  const applyMut = useMutation({
    mutationFn: () => api.applyToClan(clanName, { gamertag: gamertag.trim() || undefined, message: message.trim() || undefined }),
    onSuccess: () => {
      setApplyOpen(false);
      setError('');
      qc.invalidateQueries({ queryKey: ['myApplications'] });
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  if (isLoading) return <PageSpinner />;
  if (clanError || !clan) {
    return (
      <div className="text-center text-red-400">
        <p>Clan not found.</p>
        <Link to="/" className="mt-4 inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200">
          <ArrowLeft className="h-4 w-4" /> Back to standings
        </Link>
      </div>
    );
  }

  const seats = clan.seats ?? [];
  const members = clan.members ?? [];
  const claimed = seats.filter(s => s.status === 'CLAIMED').length;
  const ghost = seats.filter(s => s.status === 'GHOST').length;
  const pending = seats.filter(s => s.status === 'PENDING_CLAIM').length;

  const alreadyMember = !!me && members.some(m => m.discordId === me.discordId);
  const myApp = myApps?.applications?.find(a => a.clanName === clanName);
  const applyState = clanApplyState({ me, publicApplicationsOpen: clan.publicApplicationsOpen, alreadyMember, myApp });

  return (
    <div className="max-w-3xl">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-200"
      >
        <ArrowLeft className="h-4 w-4" /> Back to standings
      </Link>

      {/* Hero */}
      <ClanBrandingHero
        clanName={clan.name}
        description={clan.description}
        className="mb-4"
      />

      {/* Apply CTA — banner shows whenever the clan is taking public
          applications at all; the closed case (banner absent entirely) is
          unchanged from before. Everything inside is driven by applyState so
          an authenticated, blocked caller is told the REAL reason (already a
          member / already applied / its outcome) instead of the old
          role-gated button silently vanishing with no explanation. */}
      {clan.publicApplicationsOpen && (
        <div className="mb-6 flex items-center justify-between gap-3 rounded-lg bg-emerald-950/30 px-4 py-3">
          <div className="flex items-center gap-2">
            <DoorOpen className="h-4 w-4 shrink-0 text-emerald-300" strokeWidth={2.5} />
            <p className="text-sm text-emerald-100">{clanApplyCopy(applyState)}</p>
          </div>
          {applyState === 'apply' && (
            <button
              onClick={() => setApplyOpen(true)}
              className="shrink-0 rounded-lg bg-emerald-600 px-4 py-2 font-display text-[11px] font-black uppercase tracking-wider text-white transition-colors hover:bg-emerald-500"
            >
              <Send className="mr-1 inline h-3 w-3" strokeWidth={3} />
              Apply
            </button>
          )}
        </div>
      )}

      {/* Stats strip */}
      <div className="mb-6 grid grid-cols-3 gap-3">
        <div className="rounded-lg bg-iron-900 px-4 py-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-iron-500">Members</p>
          <p className="mt-1 font-display text-2xl font-black text-white">{members.length}</p>
        </div>
        <div className="rounded-lg bg-iron-900 px-4 py-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-iron-500">Seats filled</p>
          <p className="mt-1 font-display text-2xl font-black text-white">
            {claimed}<span className="text-iron-500">/{seats.length}</span>
          </p>
        </div>
        <div className="rounded-lg bg-iron-900 px-4 py-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-iron-500">Pending</p>
          <p className="mt-1 font-display text-2xl font-black text-white">{pending}</p>
        </div>
      </div>

      {/* Active members */}
      <Card className="mb-6">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gray-400">
          <Users className="h-4 w-4" /> Active Members
        </h2>
        {members.length === 0 ? (
          <p className="text-sm text-gray-500">No active members yet.</p>
        ) : (
          <div className="space-y-2">
            {members.map(m => (
              <div
                key={m.discordId}
                className="flex items-center justify-between rounded-lg bg-iron-900 px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  {m.role === 'LEADER' ? (
                    <Crown className="h-4 w-4 text-yellow-400" />
                  ) : (
                    <Gamepad2 className="h-4 w-4 text-gray-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-white">
                      {m.gamertag || m.displayName || <span className="text-gray-500 italic">No name</span>}
                    </p>
                  </div>
                </div>
                <StatusBadge status={m.role} />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Seat roster */}
      <Card>
        <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-gray-400">
          <Gamepad2 className="h-4 w-4" /> Seat Roster
        </h2>
        <p className="mb-4 text-xs text-gray-600">
          {claimed} claimed · {pending} pending · {ghost} ghost
        </p>
        {seats.length === 0 ? (
          <p className="text-sm text-gray-500">No seats registered yet.</p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {seats.map(seat => (
              <div
                key={seat.gamertag}
                className="flex items-center justify-between rounded-lg bg-iron-900 px-4 py-3"
              >
                <div>
                  <p className="text-sm font-medium text-white">{seat.gamertag}</p>
                  {seat.claimedByName && (
                    <p className="text-xs text-gray-500">↳ {seat.claimedByName}</p>
                  )}
                  {seat.pendingClaimantName && seat.status === 'PENDING_CLAIM' && (
                    <p className="text-xs text-yellow-500/80">Pending: {seat.pendingClaimantName}</p>
                  )}
                </div>
                <StatusBadge status={seat.status} />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Discipline (admin only) — a clan flag is recorded against the
          clan's own org (clan.orgId). Falls back to the panel's org selector
          only if the clan isn't linked to an org. */}
      {isAdminRole(me?.role) && (
        <div className="mt-6">
          <DisciplineFlagPanel
            targetType="clan"
            targetId={clan.name}
            targetName={clan.name}
            orgId={clan.orgId ?? null}
          />
        </div>
      )}

      {/* Apply modal */}
      {applyOpen && (
        <>
          <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm" onClick={() => !applyMut.isPending && setApplyOpen(false)} />
          <div className="fixed left-1/2 top-1/2 z-[80] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl bg-iron-900 p-5 shadow-elev3">
            <p className="font-display text-[10px] font-black uppercase tracking-[0.22em] text-forge-400">Apply to</p>
            <p className="mt-1 font-display text-xl font-black uppercase tracking-tight text-white">{clan.name}</p>

            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-400">
                  Your Gamertag
                </label>
                <input value={gamertag} onChange={e => setGamertag(e.target.value)} placeholder="Gamertag#tag" className="input w-full" />
              </div>
              <div>
                <label className="mb-1 block font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-400">
                  Message (optional)
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} placeholder="Tell the leader why you want in" className="input w-full resize-none" />
              </div>

              {error && <p className="rounded-lg bg-red-950/50 px-3 py-2 text-xs text-red-300">{error}</p>}

              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setApplyOpen(false)}
                  disabled={applyMut.isPending}
                  className="flex-1 rounded-lg bg-iron-800 px-3 py-2.5 font-display text-[11px] font-black uppercase tracking-wider text-iron-200 transition-colors hover:bg-iron-700 hover:text-white disabled:opacity-40"
                >
                  Cancel
                </button>
                <button
                  onClick={() => applyMut.mutate()}
                  disabled={applyMut.isPending}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-forge-500 px-3 py-2.5 font-display text-[11px] font-black uppercase tracking-wider text-iron-950 transition-colors hover:bg-forge-400 disabled:opacity-50"
                >
                  {applyMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Submit
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {applyMut.isSuccess && !applyOpen && (
        <div className="fixed bottom-6 right-6 z-[90] flex items-center gap-2 rounded-lg bg-iron-950 px-4 py-3 shadow-elev2 ring-1 ring-emerald-500/20">
          <CheckCircle2 className="h-4 w-4 text-emerald-300" strokeWidth={2.5} />
          <p className="text-sm text-white">Application sent.</p>
        </div>
      )}
    </div>
  );
}
